/**
 * The shell's run: a session (`@ab/session`) on the content pack, the dungeon
 * view (`@ab/renderer`) at the tier the shell chose, the log lines, and the
 * DOM overlays over the canvas (ADR 0005): floating labels and banners made
 * as the view reaches each event (`play`'s `onEvent`, positioned by the view's
 * `project`), and status badges from the state after each command.
 * Composition only: commands go to game-core through `send`, events go to
 * `describeEvents` (the log), `overlayFor` (labels and banners) and
 * `view.play` (the canvas). After a floor change the session has already
 * entered the next floor; its events arrive in the same result, and the view
 * builds the new floor with that floor's seed. At start the view shows floor
 * 1 once, as the session entered it; the opening events go to the log only
 * (playing them would build the same floor a second time), and its banner
 * shows.
 *
 * Touch (integration plan slice 4): `routeTo(pos)` walks the leader to a
 * tapped tile one `move` at a time. Each step's direction is game-core's:
 * the first step of the shortest walk in `walkTable` over what the fog shows
 * (`src/route.ts`: explored walkable tiles and the units the view draws),
 * recomputed before every step because units move and the explored area
 * grows; a target holding a unit the player sees is walked up to, not into.
 * After each step the route goes on only if every event of that command was
 * `moved`, `turned`, `round_ended` or natural regeneration's `healed` (cause
 * `regen`, ADR 0032) and the leader is not yet on the tile; anything else (an
 * item picked up, an attack, a potion's or a skill's heal, a floor change), a
 * refused command, no walk left, the end of the run or `cancelRoute` (any
 * key, a pad press, a second tap, a swipe) stops it after the step in
 * progress. An input that stops a route keeps its own command (`input`): it
 * waits as the one pending command and is sent once the step in progress has
 * played; a later input replaces it, and a tap while a route runs pends a
 * route to the new tile instead. The
 * snapshot carries the route's target (the shell's route marker) and the
 * minimap's view: the floor, its explored tiles (the view's fog of war) and
 * the leader's tile, re-read after each command, after a floor is shown and
 * after a floor change.
 *
 * Locales (integration plan slice 5): the controller shows one locale
 * (`setLocale` switches it) and keeps the log in every locale at once: each
 * command's events are described with each locale's table as they arrive, so
 * a switch shows the whole log in the new language without keeping old
 * states. The snapshot carries the locale and its table (`localeStrings`),
 * which every component of the shell reads.
 *
 * Sprites (integration plan slice 3): after `mount` the view loads the sheets
 * of `virtual:ab-assets` (shipped files, or the sprites repo's exports in a
 * development build); the floor shows at once, each unit as its proxy until
 * its sheet lands, then as a sprite, with no second `show`.
 *
 * The end of a run (slice 6b fix round): `onEnd` listeners hear once the run
 * has ended and its end has been shown: the last command's events have
 * played and the end banner (`run_completed`, `run_failed`, `run_escaped`)
 * has appeared on screen (at speed 0, where banners are only counted, once
 * the events have played). The page's flow starts the hold before the town
 * comes back from there. `dispose` cancels every overlay timer and empties
 * the banner queue: nothing of a departed run publishes or projects later.
 */
import type { FloorResponse, FloorResult, FloorSubmit, ResumableRun, ShownError } from '@ab/api-contract';
import { type BagEntry, type Command, type Event, type Floor, type PartyMember, type Pos, type RunState, coreVersion, createGame } from '@ab/game-core';
import { type DungeonView, type QualityTier, type ShowOptions, type Theme, mount } from '@ab/renderer';
import { type Locale, type Session, type Strings, createNamer, createRoster, currentFloorSeed, describeEvents, describeRefusal, enterFloor, floorLog, floorOver, floorSeed, locales, parseLog, replayLog, restoreSession, send, startSession, stateHash } from '@ab/session';
import type { ApiResult } from './api.ts';
import { type BadgeRow, type BannerRequest, type LabelRequest, uiTiming } from '@ab/ui';
import { pack } from 'virtual:ab-content';
import { type Overlay, badgeRows, overlayFor, stageTracker } from './overlays.ts';
import { routeGoesOn, routeStep } from './route.ts';
import { type SpritesReport, spriteLoader, tables } from './shared.ts';

export type { SpritesReport } from './shared.ts';

/** The MVP's one dungeon (ADR 0018): a `?run=1` start's; from the town, the airship's destination (ADR 0032). */
const defaultDungeon = 'dungeon.old_quarry';

export type Overlays = { readonly labels: readonly LabelRequest[]; readonly banner: BannerRequest | null; readonly badges: readonly BadgeRow[] };
/** What the minimap draws: a floor, its explored tiles (per tile index) and the leader's tile. */
export type MapView = { readonly floor: Floor; readonly explored: readonly boolean[]; readonly leader: Pos | null };
export type Snapshot = {
  readonly state: RunState;
  /** The locale the page shows and its string table (every component's text). */
  readonly locale: Locale;
  readonly strings: Strings;
  /** The log in that locale. */
  readonly lines: readonly string[];
  readonly tier: QualityTier;
  readonly overlays: Overlays;
  /** The tile a tap-to-route walk is heading for; null when no route runs. */
  readonly route: Pos | null;
  readonly map: MapView | null;
  /** A server run's floor submission was refused or got no answer (`ui.server.<code>`); the shell shows it and `retry`. */
  readonly serverError: ShownError | null;
};
/**
 * How a run starts (ADR 0034): a **local, unverified** run (`?run=1`, the dungeon tests: its seed is the page's, floors
 * follow from it, nothing goes to the server), or a **server** run: the server's run (its party, bag, the current
 * floor's seed, and the verified floors' logs when it is resumed) and how to submit a floor (`remote`).
 */
export type ControllerStart =
  | { readonly kind: 'local'; readonly seed: number; readonly dungeon?: string; readonly party?: readonly PartyMember[] }
  | { readonly kind: 'server'; readonly run: ResumableRun; readonly remote: RunRemote };

/** Submits a finished floor to the server (`POST /api/runs/:id/floors/:n`). */
export type RunRemote = { submit(floor: number, body: FloorSubmit): Promise<ApiResult<FloorResponse>> };

/** The end of a server run, as the server decided it: completed, failed or escaped, and the egg it granted. */
export type ServerEnd = Extract<FloorResult, { readonly result: unknown }>['result'];

export type SendOutcome = { readonly ok: true; readonly events: number } | { readonly ok: false; readonly message: string };
export type ReplayReport = {
  readonly match: boolean;
  readonly expected: string;
  readonly actual: string;
  readonly error: string | null;
  readonly steps: number;
  /** Events of each type in the log's replay, and how many of each the view handled. */
  readonly eventTypes: Readonly<Record<string, number>>;
  readonly handled: Readonly<Record<string, number>>;
  /** Labels and banners the shell made during the replay, by event type. */
  readonly labels: Readonly<Record<string, number>>;
  readonly banners: Readonly<Record<string, number>>;
};


export type Controller = {
  /** The current view (a `remount` replaces it). */
  readonly view: DungeonView;
  readonly theme: Theme;
  readonly snapshot: () => Snapshot;
  readonly subscribe: (listener: () => void) => () => void;
  /** True while a command's events are playing (keys are ignored then). */
  busy(): boolean;
  /** Sends one command, logs and plays its events; refused while an animation plays or after the run ended. */
  send(command: Command): Promise<SendOutcome>;
  /**
   * A player's command (a key, a pad button, a swipe): while a route runs it
   * stops the route and waits as the one pending command, sent once the step
   * in progress has played (a later input replaces it); otherwise `send`.
   */
  input(command: Command): void;
  /**
   * Walks the leader to `pos` one `move` at a time (game-core picks each step);
   * resolves when the route stops. While a route runs it stops that route and
   * pends this one instead (it starts once the step in progress has played).
   * Not started while a command plays or after the run ended.
   */
  routeTo(pos: Pos): Promise<void>;
  /** Stops a running route after the step in progress. */
  cancelRoute(): void;
  /** The tile the running route heads for; null when none runs. */
  routing(): Pos | null;
  /** The commands game-core accepted since mount, in order (tests count a route's moves). */
  commands(): readonly Command[];
  /** How many sends were refused because a command was still playing (a held pad button must never cause one). */
  busyRefusals(): number;
  /** How many commands game-core refused (an illegal move, say): each puts a line in the log. */
  refusals(): number;
  /** Sizes the view to the canvas's settled CSS size; counted (`resizes`). */
  resize(width: number, height: number): void;
  resizes(): number;
  /** Replays a golden log through the session package and feeds every step's events to the view. */
  replay(logText: string): Promise<ReplayReport>;
  /**
   * Shows floor `floorNumber` of this run's seed as it is entered, with the
   * leader moved to `at` if given (presentation only: the session is not
   * touched). For the designer's captures; returns the state shown.
   */
  preview(floorNumber: number, at?: Pos): RunState;
  /** Switches the text to `locale` (the log is re-read in it too) and publishes it; the shell remembers a player's choice. */
  setLocale(locale: Locale): void;
  /** Switches the renderer's tier and publishes it (the HUD shows it). */
  setTier(tier: QualityTier): void;
  /** 1 is normal speed; 0 makes every animation instant (overlays are then counted, not shown). */
  setSpeed(speed: number): void;
  /** Plays events that did not come from a command, as the shell plays a command's (tests: a case no golden log has); badges from `after`. */
  playScripted(events: readonly Event[], before: RunState, after: RunState): Promise<void>;
  /** Labels and banners made since mount, by event type. */
  overlayCounts(): { readonly labels: Readonly<Record<string, number>>; readonly banners: Readonly<Record<string, number>> };
  /** Disposes the view and mounts a new one on the same canvas at `tier`; returns what the old one left on the GPU. */
  remount(tier: QualityTier): { readonly geometries: number; readonly textures: number };
  /** The sprite sheets: loaded, their origins, still loading. */
  sprites(): SpritesReport;
  /** Calls `listener` as the view starts each event of a command (tests read the view at that moment); returns the unsubscribe. */
  watchEvents(listener: (event: Event) => void): () => void;
  /**
   * Calls `listener` once, when the run has ended and its end has shown: its last events played and its end banner
   * on screen (at speed 0, once the events played). Returns the unsubscribe.
   */
  onEnd(listener: () => void): () => void;
  /** A server run's end as the server answered it (null: a local run, or not over yet). */
  serverEnd(): ServerEnd | null;
  /** After `serverError`: sends the floor again (no answer, a server error, too many tries, logged in again), or gives the run up on the page (any other refusal: it ends). */
  retry(): void;
  /** Frees the view (the town takes the canvas back, slice 6b) and cancels the overlays' timers; returns what the view left on the GPU. */
  dispose(): { readonly geometries: number; readonly textures: number };
};

/** The events whose banner says the run is over: the hold before the town comes back starts once it shows. */
const endEvents: ReadonlySet<Event['type']> = new Set(['run_completed', 'run_failed', 'run_escaped']);

const terminal = (state: RunState): boolean => state.phase === 'completed' || state.phase === 'failed' || state.phase === 'escaped';

/** Each item's floor pattern (its content record's `icon`). */
const itemIcons: ShowOptions['itemIcons'] = Object.fromEntries(Object.values(pack.items).map((item) => [item.id, item.icon]));


/** Whether this build can play a server run: the run is pinned to a game-core and content this bundle carries (ADR 0002). */
export const playable = (run: { readonly coreVersion: string; readonly contentHash: string }): boolean => run.coreVersion === coreVersion && run.contentHash === pack.hash;

/**
 * Which refusals of a floor's submission a second try can change (ADR 0034 D9): no answer (`offline`), a server
 * error (`error`), too many tries (`rate_limited`) and `unauthorized` (the login shows; once logged in, the shell sends
 * the floor again). Closing any other refusal's line gives the run up on this page (it ends, unverified, and the page
 * goes back to the ship); the server keeps it `playing` until the next run starts or the player gives it up.
 */
const retryable = (error: ShownError): boolean => error === 'offline' || error === 'error' || error === 'rate_limited' || error === 'unauthorized';

/**
 * A run (ADR 0034): a local one of its dungeon with its party (the default party unless given; `?run=1`), or the
 * server's (the party, bag and seeds the server gave; a resumed one replays its verified floors first, `restoreSession`).
 */
export const createController = (canvas: HTMLCanvasElement, begin: ControllerStart, tier: QualityTier, locale: Locale): Controller => {
  const game = createGame(pack);
  const dungeonId = begin.kind === 'local' ? (begin.dungeon ?? defaultDungeon) : begin.run.dungeon;
  const dungeon = pack.dungeons[dungeonId];
  const theme = dungeon === undefined ? undefined : pack.themes[dungeon.theme];
  if (dungeon === undefined || theme === undefined) throw new Error(`${dungeonId} or its theme is missing from the content pack`);
  const remote = begin.kind === 'server' ? begin.remote : null;
  const party: readonly PartyMember[] = begin.kind === 'local' ? (begin.party ?? pack.rules.newPlayer.defaultParty) : begin.run.party;
  const bag: readonly BagEntry[] = begin.kind === 'local' ? pack.rules.newPlayer.starterBag : begin.run.bag;
  const started =
    begin.kind === 'local'
      ? { ...startSession(game, dungeonId, party, begin.seed), before: game.createRun(dungeonId, party, bag) }
      : restoreSession(game, { dungeon: dungeonId, party, bag, floors: begin.run.floors.map((f) => f.log as unknown as Command[]), seed: begin.run.seed });
  const start = started.before;
  const session: Session = started.session;
  /** The seed of the floor on screen (the last `enter_floor`: the run seed\'s floor, or the server\'s answer). */
  const currentSeed = (): number => currentFloorSeed(session) ?? session.seed;
  /** The run's log memory (ADR 0032): each enemy's letter ("Slime A") from command to command, and the floors' direction (B1F). */
  const roster = createRoster(dungeon.direction);
  const showCurrent = (v: DungeonView): void => {
    v.resize(canvas.clientWidth, canvas.clientHeight);
    v.show(session.state, { seed: currentSeed(), theme, itemIcons });
  };
  /** Loads the sheets into a view (a sheet this page already prepared installs at once, before the first frame). */
  const spritesOf = spriteLoader();
  const loadSprites = (v: DungeonView): void => {
    spritesOf.load(v);
  };
  let view = mount(canvas, { tier });
  loadSprites(view);
  showCurrent(view);
  const eventListeners = new Set<(event: Event) => void>();
  const listeners = new Set<() => void>();
  const leaderPos = (state: RunState): Pos | null => state.units.find((u) => u.id === state.party[0])?.pos ?? null;
  const mapOf = (state: RunState, at?: Pos): MapView | null => (state.floor === null ? null : { floor: state.floor, explored: view.explored(), leader: at ?? leaderPos(state) });
  /**
   * The log in every locale at once (a command's lines are described three times; the tables are small), so a
   * language switch shows the whole log in the new language without keeping the states each line came from.
   */
  const log = Object.fromEntries(locales.map((l) => [l, [] as string[]])) as Record<Locale, string[]>;
  const describeAll = (lines: (table: Strings) => readonly string[]): void => {
    for (const l of locales) log[l].push(...lines(tables[l]));
  };
  describeAll((table) => describeEvents(table, started.events, start, session.state, roster));
  let snap: Snapshot = {
    state: session.state,
    locale,
    strings: tables[locale],
    lines: [...log[locale]],
    tier,
    overlays: { labels: [], banner: null, badges: badgeRows(session.state) },
    route: null,
    map: mapOf(session.state),
    serverError: null,
  };
  const accepted: Command[] = [];
  let refusedBusy = 0;
  let refused = 0;
  /** What waits for a cancelled route's step to finish: one command, or a route to a new tile. */
  let pending: { readonly kind: 'command'; readonly command: Command } | { readonly kind: 'route'; readonly target: Pos } | null = null;
  let resizeCount = 0;
  let route: { readonly target: Pos; cancelled: boolean } | null = null;
  let sending = false;
  let speed = 1;
  let nextId = 1;
  let disposed = false;
  /** The overlays' pending timeouts (labels, banners): `dispose` cancels them. */
  const timers = new Set<number>();
  const later = (act: () => void, ms: number): void => {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      if (!disposed) act();
    }, ms);
    timers.add(timer);
  };
  /** When each unit may show its next label (labels of one unit are spaced by `uiTiming.labelGapMs`). */
  const nextLabelAt = new Map<string, number>();
  /** Banners waiting for the one on screen to have held its minimum time. */
  const bannerQueue: BannerRequest[] = [];
  let bannerHolding = false;
  /**
   * The end of the run as shown: `endMade` once an end event made its banner (or, at speed 0, counted it),
   * `endShown` once that banner is on screen; `ended` once the listeners heard.
   */
  let endMade = false;
  let endShown = false;
  let ended = false;
  const endBanners = new Set<number>();
  const endWaiters: (() => void)[] = [];
  const endListeners = new Set<() => void>();
  const markEndShown = (): void => {
    endShown = true;
    for (const w of endWaiters.splice(0)) w();
  };
  const nextBanner = (): void => {
    const b = bannerQueue.shift();
    if (b === undefined) return;
    bannerHolding = true;
    setOverlays((o) => ({ ...o, banner: b }));
    if (endBanners.has(b.id)) markEndShown();
    later(() => {
      bannerHolding = false;
      nextBanner();
    }, uiTiming.bannerMinHold / speed);
    later(() => {
      setOverlays((o) => (o.banner?.id === b.id ? { ...o, banner: null } : o));
    }, b.durationMs);
  };
  /** After a command's (or a scripted) play: once the run is over and its end banner has shown, the end listeners hear. */
  const announceEnd = async (after: RunState): Promise<void> => {
    if (ended || !terminal(after)) return;
    if (endMade && !endShown) await new Promise<void>((resolve) => endWaiters.push(resolve));
    if (ended || disposed) return;
    ended = true;
    for (const l of [...endListeners]) l();
  };
  const labelCounts: Record<string, number> = {};
  const bannerCounts: Record<string, number> = {};
  const notify = (): void => {
    for (const l of listeners) l();
  };
  const setOverlays = (change: (o: Overlays) => Overlays): void => {
    if (disposed) return;
    snap = { ...snap, overlays: change(snap.overlays) };
    notify();
  };
  /** The minimap's view from the floor the view shows now (after a command, a `show`, a floor change). */
  const refreshMap = (state: RunState, at?: Pos): void => {
    snap = { ...snap, map: mapOf(state, at) };
    notify();
  };
  /** Adds a command's lines (described in every locale by `lines`) to the log and publishes the new state. */
  const publish = (lines: (table: Strings) => readonly string[]): void => {
    describeAll(lines);
    snap = { ...snap, state: session.state, lines: [...log[snap.locale]] };
    notify();
  };

  /** Turns an overlay into a label or banner on screen (at speed 0 it is only counted). */
  const show = (type: Event['type'], overlay: Overlay): void => {
    if (overlay === null) return;
    const counts = 'label' in overlay ? labelCounts : bannerCounts;
    counts[type] = (counts[type] ?? 0) + 1;
    const end = endEvents.has(type);
    if (end) endMade = true;
    if (speed === 0) {
      // Counted, not shown: the end is as shown as it will be.
      if (end) markEndShown();
      return;
    }
    const id = nextId;
    nextId += 1;
    if ('label' in overlay) {
      const { unit, kind, key, values, names, keys } = overlay.label;
      const durationMs = uiTiming.labels[kind] / speed;
      // Labels of one unit that come together (exp, a level, a skill learned) follow each other with a short
      // gap, so each has risen clear of the next instead of overlapping it; each is placed when it shows.
      const now = performance.now();
      const showAt = unit === null ? now : Math.max(now, nextLabelAt.get(unit) ?? 0);
      if (unit !== null) nextLabelAt.set(unit, showAt + uiTiming.labelGapMs / speed);
      later(() => {
        const at = unit === null ? null : view.project(unit);
        const request: LabelRequest = {
          id,
          kind,
          key,
          at: at === null ? null : { x: at.x, y: at.y },
          visible: at === null || at.visible,
          durationMs,
          ...(values ? { values } : {}),
          ...(names ? { names } : {}),
          ...(keys ? { keys } : {}),
        };
        setOverlays((o) => ({ ...o, labels: [...o.labels, request] }));
        later(() => {
          setOverlays((o) => ({ ...o, labels: o.labels.filter((l) => l.id !== id) }));
        }, durationMs);
      }, showAt - now);
    } else {
      if (end) endBanners.add(id);
      bannerQueue.push({ id, durationMs: uiTiming.banner / speed, ...overlay.banner });
      if (!bannerHolding) nextBanner();
    }
  };

  const play = (events: readonly Event[], before: RunState, after: RunState, next?: { readonly seed: number }): Promise<void> => {
    const stages = stageTracker(before, dungeon.direction);
    return view.play(events, before, after, {
      ...(next === undefined ? {} : { seed: next.seed }),
      onEvent: (e) => {
        show(e.type, overlayFor(e, before, after, stages));
        stages.update(e);
        for (const l of eventListeners) l(e);
      },
    });
  };

  /** Sends one command and plays its events; the events come back for the route's stop rule. */
  const dispatch = async (command: Command): Promise<{ readonly outcome: SendOutcome; readonly events: readonly Event[] }> => {
    if (sending || view.busy) {
      refusedBusy += 1;
      return { outcome: { ok: false, message: 'busy' }, events: [] };
    }
    if (terminal(session.state)) return { outcome: { ok: false, message: 'the run is over' }, events: [] };
    sending = true;
    try {
      const r = send(session, command);
      if (!r.ok) {
        refused += 1;
        // Said from game-core's refusal code (`ui.refusal.<code>`), in each locale's words.
        const state = session.state;
        publish((table) => [describeRefusal(createNamer(table, [state], [], roster), r)]);
        return { outcome: r, events: [] };
      }
      accepted.push(command);
      // A server run: a finished floor goes to the server before anything plays; the next floor is entered with the
      // seed it answers (ADR 0002), so the descent and the new floor play together as a local run's do.
      const events = [...r.events];
      if (remote !== null && floorOver(session)) {
        const verdict = await settle(remote);
        if (verdict === null) return { outcome: { ok: true, events: events.length }, events };
        if ('next' in verdict) {
          const entered = enterFloor(session, verdict.next.seed);
          if (!entered.ok) throw new Error(entered.message);
          events.push(...entered.events);
        } else {
          serverEnd = verdict.result;
        }
      }
      const after = session.state;
      publish((table) => describeEvents(table, events, r.before, after, roster));
      await play(events, r.before, session.state, { seed: currentSeed() });
      setOverlays((o) => ({ ...o, badges: badgeRows(session.state) }));
      refreshMap(session.state);
      void announceEnd(session.state);
      return { outcome: { ok: true, events: events.length }, events };
    } finally {
      sending = false;
    }
  };

  /** What the server answered the last floor of a server run, once it ended. */
  let serverEnd: ServerEnd | null = null;
  /** Waits for the player to dismiss a submission's refusal: true sends again, false gives the run up. */
  let retryWaiter: ((again: boolean) => void) | null = null;
  /**
   * Submits the finished floor (its log from its `enter_floor`, the state hash, the versions this bundle plays with)
   * until the server answers it. A refusal a second try can change (`retryable`: no answer, a server error, too many
   * tries, `unauthorized` while the login shows) shows its line and waits for the player's tap (or the new login) to
   * send it again; any other refusal (a log the server could not verify, a version it does not run) shows its line and
   * gives the run up on this page: it ends here, unverified, and nothing of it is granted (the server keeps the run
   * `playing` until the next one starts). Null when given up or disposed.
   */
  const settle = async (to: RunRemote): Promise<FloorResult | null> => {
    const body: FloorSubmit = { log: floorLog(session), stateHash: await stateHash(session.state), coreVersion, contentHash: pack.hash };
    const floor = session.state.floorNumber;
    for (;;) {
      const r = await to.submit(floor, body);
      if (disposed) return null;
      if (r.ok) return 'next' in r.value ? { next: r.value.next } : { result: r.value.result };
      snap = { ...snap, serverError: r.error };
      notify();
      const again = await new Promise<boolean>((resolve) => {
        retryWaiter = resolve;
      });
      snap = { ...snap, serverError: null };
      notify();
      if (disposed) return null;
      if (!again || !retryable(r.error)) {
        // Given up: the run is over as far as the page goes; the flow goes back to the ship.
        ended = true;
        for (const l of [...endListeners]) l();
        return null;
      }
    }
  };

  const routeTo = async (pos: Pos): Promise<void> => {
    if (route !== null) {
      route.cancelled = true;
      pending = { kind: 'route', target: pos };
      return;
    }
    if (sending || view.busy || terminal(session.state)) return;
    const r = { target: pos, cancelled: false };
    route = r;
    snap = { ...snap, route: pos };
    notify();
    try {
      for (;;) {
        const state = session.state;
        const at = leaderPos(state);
        if (r.cancelled || terminal(state) || state.floor === null || at === null || (at.x === pos.x && at.y === pos.y)) break;
        // Game-core's shortest walk over what the fog shows, from where the leader stands now (units move and the
        // explored area grows, so it is asked again every step).
        const dir = routeStep(state, view.explored(), at, pos);
        if (dir === null) break;
        const { outcome, events } = await dispatch({ type: 'move', dir });
        if (!outcome.ok || !events.every(routeGoesOn)) break;
      }
    } finally {
      route = null;
      snap = { ...snap, route: null };
      notify();
    }
    // The input that stopped the route, now that its step has played.
    const next = pending;
    pending = null;
    if (next === null) return;
    switch (next.kind) {
      case 'command':
        await dispatch(next.command);
        return;
      case 'route':
        await routeTo(next.target);
        return;
    }
  };

  const opening = started.events.find((e) => e.type === 'floor_entered');
  if (opening !== undefined) show(opening.type, overlayFor(opening, start, session.state, stageTracker(start, dungeon.direction)));

  return {
    get view() {
      return view;
    },
    theme,
    snapshot: () => snap,
    busy: () => sending || view.busy,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    send: async (command) => (await dispatch(command)).outcome,
    input(command) {
      if (route === null) {
        void dispatch(command);
        return;
      }
      route.cancelled = true;
      pending = { kind: 'command', command };
    },
    routeTo,
    cancelRoute() {
      if (route !== null) route.cancelled = true;
    },
    routing: () => route?.target ?? null,
    commands: () => [...accepted],
    busyRefusals: () => refusedBusy,
    refusals: () => refused,
    resize(width, height) {
      resizeCount += 1;
      view.resize(width, height);
    },
    resizes: () => resizeCount,
    async replay(logText) {
      const r = await replayLog(game, parseLog(logText));
      const before = view.eventCounts();
      const labelsBefore = { ...labelCounts };
      const bannersBefore = { ...bannerCounts };
      const eventTypes: Record<string, number> = {};
      let shown = false;
      for (const step of r.steps) {
        for (const e of step.events) eventTypes[e.type] = (eventTypes[e.type] ?? 0) + 1;
        const next = step.command.type === 'enter_floor' ? { seed: step.command.seed } : undefined;
        if (next !== undefined && !shown) {
          view.show(step.after, { seed: next.seed, theme, itemIcons });
          shown = true;
        }
        await play(step.events, step.before, step.after, next);
      }
      const last = r.steps.at(-1);
      if (last !== undefined) refreshMap(last.after);
      const after = view.eventCounts();
      const handled: Record<string, number> = {};
      const labels: Record<string, number> = {};
      const banners: Record<string, number> = {};
      for (const type of Object.keys(eventTypes)) {
        const key = type as Event['type'];
        handled[type] = (after[key] ?? 0) - (before[key] ?? 0);
        labels[type] = (labelCounts[type] ?? 0) - (labelsBefore[type] ?? 0);
        banners[type] = (bannerCounts[type] ?? 0) - (bannersBefore[type] ?? 0);
      }
      return { match: r.match, expected: r.expected, actual: r.actual, error: r.error?.message ?? null, steps: r.steps.length, eventTypes, handled, labels, banners };
    },
    preview(floorNumber, at) {
      const fresh = game.createRun(dungeonId, party, bag);
      const entered = game.apply({ ...fresh, floorNumber: floorNumber - 1 }, { type: 'enter_floor', seed: floorSeed(session.seed, floorNumber) }).state;
      // The floor and its light are built from the state as entered; only the leader's drawing and the camera move.
      view.show(entered, { seed: floorSeed(session.seed, floorNumber), theme, itemIcons, ...(at === undefined ? {} : { leaderAt: at }) });
      refreshMap(entered, at);
      return entered;
    },
    setLocale(next) {
      snap = { ...snap, locale: next, strings: tables[next], lines: [...log[next]] };
      notify();
    },
    setTier(next) {
      view.setTier(next);
      snap = { ...snap, tier: view.tier };
      notify();
    },
    setSpeed(next) {
      speed = next;
      view.setSpeed(next);
    },
    async playScripted(events, before, after) {
      await play(events, before, after);
      setOverlays((o) => ({ ...o, badges: badgeRows(after) }));
      refreshMap(after);
      void announceEnd(after);
    },
    overlayCounts: () => ({ labels: { ...labelCounts }, banners: { ...bannerCounts } }),
    remount(next) {
      const left = view.dispose();
      view = mount(canvas, { tier: next });
      view.setSpeed(speed);
      loadSprites(view);
      showCurrent(view);
      snap = { ...snap, tier: view.tier, map: mapOf(session.state) };
      notify();
      return left;
    },
    sprites: () => spritesOf.report(),
    serverEnd: () => serverEnd,
    retry() {
      const w = retryWaiter;
      retryWaiter = null;
      w?.(true);
    },
    watchEvents(listener) {
      eventListeners.add(listener);
      return () => eventListeners.delete(listener);
    },
    onEnd(listener) {
      endListeners.add(listener);
      return () => endListeners.delete(listener);
    },
    dispose() {
      disposed = true;
      retryWaiter?.(false);
      retryWaiter = null;
      if (route !== null) route.cancelled = true;
      for (const timer of timers) window.clearTimeout(timer);
      timers.clear();
      bannerQueue.length = 0;
      nextLabelAt.clear();
      endWaiters.length = 0;
      endListeners.clear();
      listeners.clear();
      eventListeners.clear();
      return view.dispose();
    },
  };
};
